import { useEffect, useState } from "react";
import { RefreshCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, CAP_LABELS, fmtErr } from "@/lib/api";

const CAPS = ["push", "pull", "squat", "bend_lift", "rotation", "endurance"];

export default function PlannerTab() {
  const [groups, setGroups] = useState([]);
  const [members, setMembers] = useState([]);
  const [owner, setOwner] = useState("grp_main");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [cap, setCap] = useState("squat");
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState(null);

  useEffect(() => {
    api.get("/coach/groups").then((r) => setGroups(r.data)).catch(() => {});
    api.get("/coach/members").then((r) => setMembers(r.data)).catch(() => {});
  }, []);

  const loadPlans = (o = owner) =>
    api.get(`/coach/plans?owner_id=${o}`).then((r) => setPlans(r.data)).catch(() => {});
  useEffect(() => { loadPlans(owner); setCurrent(null); }, [owner]);

  const generate = async () => {
    try {
      const payload = { date, primary_capability: cap };
      if (owner.startsWith("grp_")) payload.group_id = owner; else payload.member_id = owner;
      const { data } = await api.post("/coach/plans/generate", payload);
      setCurrent(data);
      toast.success("Session generated — live for all members of this group");
      loadPlans();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const remove = async (id) => {
    await api.delete(`/coach/plans/${id}`);
    toast.success("Plan deleted");
    if (current?.id === id) setCurrent(null);
    loadPlans();
  };

  return (
    <div data-testid="coach-planner">
      <h2 className="font-display uppercase text-4xl mb-2">Plan <span className="text-volt">builder.</span></h2>
      <p className="text-xs text-zinc-500 mb-6">The engine enforces the 6-block structure, one primary capability, varied core and max 2 animal drills — automatically.</p>

      <div className="flex flex-wrap gap-3 items-end border border-zinc-800 bg-zinc-950 p-5">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-1.5">Assign to</p>
          <select data-testid="planner-owner" value={owner} onChange={(e) => setOwner(e.target.value)}
            className="bg-ink border border-zinc-700 px-4 py-3 text-sm focus:border-volt outline-none min-w-[220px]">
            <optgroup label="Groups">
              {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </optgroup>
            <optgroup label="Individuals">
              {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
            </optgroup>
          </select>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-1.5">Date</p>
          <input data-testid="planner-date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
            className="bg-ink border border-zinc-700 px-4 py-3 text-sm focus:border-volt outline-none [color-scheme:dark]" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-1.5">Primary capability</p>
          <select data-testid="planner-capability" value={cap} onChange={(e) => setCap(e.target.value)}
            className="bg-ink border border-zinc-700 px-4 py-3 text-sm focus:border-volt outline-none">
            {CAPS.map((c) => <option key={c} value={c}>{CAP_LABELS[c]}</option>)}
          </select>
        </div>
        <button data-testid="planner-generate" onClick={generate}
          className="bg-volt text-black text-xs uppercase tracking-[0.16em] font-extrabold px-6 py-3.5 hover:bg-white transition-colors">
          Generate session
        </button>
      </div>

      {current && (
        <div className="mt-6 border border-volt/40 bg-zinc-950" data-testid="planner-result">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-800 p-5">
            <div>
              <p className="font-display uppercase text-3xl">{current.name}</p>
              <p className="text-xs text-zinc-400 mt-1">{current.date} · {current.duration_minutes} min · RPE {current.target_rpe}</p>
              <p className="text-sm text-zinc-300 mt-2 max-w-xl">{current.objective}</p>
            </div>
            <button data-testid="planner-regenerate" onClick={generate}
              className="inline-flex items-center gap-2 border border-zinc-700 px-4 py-3 text-xs uppercase tracking-[0.14em] font-bold hover:border-volt hover:text-volt transition-colors">
              <RefreshCcw size={14} /> Regenerate
            </button>
          </div>
          <div className="grid md:grid-cols-2 xl:grid-cols-3">
            {current.blocks.map((b) => (
              <div key={b.key} className="p-5 border-b xl:border-r border-zinc-800/70">
                <div className="flex items-baseline justify-between">
                  <p className="font-display uppercase text-xl">{b.name}</p>
                  <span className="text-[10px] text-volt font-bold uppercase tracking-[0.16em]">{b.minutes}m</span>
                </div>
                <ul className="mt-3 space-y-2">
                  {b.items.map((i) => (
                    <li key={i.movement_id} className="text-sm">
                      <div className="flex justify-between gap-2">
                        <span className="text-zinc-200">{i.name}</span>
                        <span className="text-zinc-500 text-xs whitespace-nowrap">{i.prescription}</span>
                      </div>
                      <p className="text-[10px] text-zinc-600 uppercase tracking-[0.08em]">
                        {i.regression && `R: ${i.regression}`}{i.regression && i.progression && " · "}{i.progression && `P: ${i.progression}`}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8">
        <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold mb-3">Scheduled plans for this owner</p>
        <div className="border border-zinc-800 divide-y divide-zinc-800/70">
          {plans.map((p) => (
            <div key={p.id} data-testid={`plan-row-${p.id}`} className="flex items-center justify-between p-4 bg-zinc-950">
              <div>
                <p className="text-sm font-bold text-zinc-100">{p.name}</p>
                <p className="text-xs text-zinc-500">{p.date} · {p.duration_minutes} min</p>
              </div>
              <div className="flex gap-2 items-center">
                <button onClick={async () => { await api.put(`/coach/plans/${p.id}`, { published: !p.published }); loadPlans(); }}
                  data-testid={`plan-publish-${p.id}`}
                  className={`text-xs uppercase tracking-[0.14em] font-bold px-3 py-2 transition-colors ${p.published ? "text-volt" : "text-zinc-500 hover:text-volt"}`}>
                  {p.published ? "Published" : "Publish"}
                </button>
                <button onClick={() => setCurrent(p)} data-testid={`plan-view-${p.id}`}
                  className="text-xs uppercase tracking-[0.14em] font-bold text-zinc-400 hover:text-volt transition-colors px-3 py-2">View</button>
                <button onClick={() => remove(p.id)} data-testid={`plan-delete-${p.id}`}
                  className="text-zinc-600 hover:text-red-400 transition-colors px-2" aria-label="Delete plan">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
          {!plans.length && <p className="p-4 text-sm text-zinc-600">No plans yet — generate one above.</p>}
        </div>
      </div>
    </div>
  );
}
